/**
 * JMD Produktside – Familie Layout
 * 1) <familie-figure-preview>: live figur-forhåndsvisning — lytter på de
 *    eksisterende .family-color-input radios (family-color-selector.js) og
 *    toner figurerne i den valgte farve.
 * 2) Scroll-CTA: "Design din familie ↓" → smooth scroll til #konfigurator.
 * 3) Sticky-bar qty-spejling: antal fra hovedformen spejles ind i sticky-formen.
 * 4) Hero-anmeldelseskort: erstatter de kuraterede citater med produktets
 *    bedste ægte Judge.me-anmeldelser (fald tilbage til kuraterede pr. kort).
 *
 * Docs: docs/FAMILIE_PRODUKTSIDE_2026.md
 */

(function () {
  'use strict';

  function cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  /* ── 1) Live figur-forhåndsvisning ── */
  if (!customElements.get('familie-figure-preview')) {
    class FamilieFigurePreview extends HTMLElement {
      connectedCallback() {
        var self = this;
        this._onChange = function (e) {
          if (e.target && e.target.matches && e.target.matches('.family-color-input')) {
            self.paint(e.target);
          }
        };
        // Dokument-delegeret: radio-change bobler, og previewet er afkoblet
        // fra selectorens DOM-placering (kan stå i en anden kolonne/region).
        document.addEventListener('change', this._onChange);

        // family-color-selector.js sender dette når et medlem nulstilles
        // (deselectMember/resetAll) — der er ingen 'change'-event ved fravalg.
        this._onClear = function (e) {
          if (e.detail && e.detail.member) self.clear(e.detail.member);
        };
        document.addEventListener('family-color:clear', this._onClear);

        // Gendan state ved bfcache/back-nav eller theme editor re-render
        document.querySelectorAll('.family-color-input:checked').forEach(function (input) {
          self.paint(input);
        });
      }

      disconnectedCallback() {
        if (this._onChange) document.removeEventListener('change', this._onChange);
        if (this._onClear) document.removeEventListener('family-color:clear', this._onClear);
      }

      paint(input) {
        if (!input.dataset.member) return;
        var fig = this.querySelector('.pf-figure[data-member="' + cssEscape(input.dataset.member) + '"]');
        if (!fig) return;

        var label = input.nextElementSibling; // .family-color-swatch
        var hex = label ? label.dataset.optionSwatchValue : null;

        // SVG-figuren farves via currentColor → sæt CSS color på .pf-figure
        if (hex) {
          fig.style.color = hex;
          fig.classList.add('is-chosen');
        }
      }

      clear(memberId) {
        var fig = this.querySelector('.pf-figure[data-member="' + cssEscape(memberId) + '"]');
        if (!fig) return;
        fig.style.removeProperty('color');
        fig.classList.remove('is-chosen');
      }
    }

    customElements.define('familie-figure-preview', FamilieFigurePreview);
  }

  /* ── 2) Scroll-CTA → #konfigurator ── */
  document.addEventListener('click', function (e) {
    var cta = e.target.closest ? e.target.closest('[data-familie-scroll]') : null;
    if (!cta) return;
    var el = document.getElementById('konfigurator');
    if (!el) return; // fald tilbage til native anker-hop
    e.preventDefault();
    var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // scroll-margin-top på #konfigurator håndterer sticky-header offset
    el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  });

  /* ── 3) Sticky-bar qty-spejling (samme mønster som discount-tiers.js) ── */
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.matches || !e.target.matches('.product-familie-section input[name="quantity"]')) return;
    var stickyForm = document.querySelector('pd-sticky-bar form');
    if (!stickyForm) return;
    var qtyInput = stickyForm.querySelector('input[name="quantity"]');
    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      stickyForm.appendChild(qtyInput);
    }
    qtyInput.value = e.target.value;
  });

  /* ── 4) Hero-anmeldelseskort fra Judge.me ──
     Læser produktets rigtige anmeldelser fra Judge.me-widgeten (renderes
     klient-side) og fylder hero-kortene med de bedste 5-stjernede. Hvert kort
     beholder sin kuraterede fallback-tekst, hvis der ikke er en ægte anmeldelse
     til den plads. Judge.me loader asynkront → poll + MutationObserver. */
  (function initHeroReviews() {
    var container = document.querySelector('.pf-hero__cards[data-pf-auto-reviews="true"]');
    if (!container) return;
    var cards = container.querySelectorAll('[data-pf-hero-card]');
    if (!cards.length) return;
    var done = false;

    /* Understøtter både Judge.me Widget V3 (.jm-*) og legacy (.jdgm-*) — samme
       selektorer som main-product-2026.js. Kun .jdgm-* her betød at V3-widget'en
       (live siden sommer 2026) aldrig matchede, så de kuraterede kort aldrig
       blev afløst af ægte anmeldelser. */
    function scoreOf(rev) {
      var v3 = rev.querySelector('.jm-star-rating');
      if (v3) {
        var aria = v3.getAttribute('aria-label') || '';
        var m = aria.match(/([\d.,]+)\s*out of/);
        if (m) return parseFloat(m[1].replace(',', '.')) || 0;
      }
      var r = rev.querySelector('.jdgm-rev__rating');
      if (r && r.getAttribute('data-score')) return parseFloat(r.getAttribute('data-score')) || 0;
      return rev.querySelectorAll('.jdgm-star.jdgm--on').length;
    }
    function textOf(rev, sel) {
      var el = rev.querySelector(sel);
      return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
    }
    function truncate(s, n) {
      if (s.length <= n) return s;
      return s.slice(0, n - 1).replace(/\s+\S*$/, '') + '…';
    }
    function collect() {
      var out = [];
      document.querySelectorAll('.jm-review-item, .jdgm-rev').forEach(function (rev) {
        var body = textOf(rev, '.jm-review-content__body, .jdgm-rev__body');
        if (!body) body = textOf(rev, '.jm-review-content__title');
        if (scoreOf(rev) >= 4.5 && body.length >= 20) {
          out.push({ body: body, author: textOf(rev, '.jm-reviewer-info__name, .jdgm-rev__author') });
        }
      });
      return out;
    }
    function hydrate() {
      if (done) return;
      var reviews = collect();
      if (!reviews.length) return; // ingen ægte endnu → behold kuraterede
      done = true;
      cards.forEach(function (card, i) {
        var r = reviews[i];
        if (!r) return; // ingen ægte til denne plads → behold kurateret
        var q = card.querySelector('[data-pf-card-quote]');
        var a = card.querySelector('[data-pf-card-author]');
        if (q) q.textContent = '“' + truncate(r.body, 150) + '”';
        if (a) a.textContent = '— ' + (r.author || 'Verificeret køb') + ' · verificeret køb';
        card.setAttribute('data-pf-real', 'true');
      });
    }

    var tries = 0;
    var iv = setInterval(function () {
      hydrate();
      if (done || ++tries > 20) clearInterval(iv);
    }, 700);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hydrate);
    } else {
      hydrate();
    }

    // Fang Judge.me's asynkrone render
    var obs = new MutationObserver(function () { hydrate(); if (done) obs.disconnect(); });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 16000);
  })();
})();
